import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { HardHat, Plus, Paperclip, MagnifyingGlass } from "@phosphor-icons/react";

const STATUS_CLASS = { draft: "bg-slate-100 text-slate-700", issued: "bg-emerald-100 text-emerald-700" };

/**
 * Shown right after a job request is converted to a job: create a fresh RAMS for the new job,
 * attach one already written for the request (or any other RAMS), or skip.
 */
export default function RamsAfterConvertDialog({ open, jobId, jobRef, requestId, onClose, autoPending = false }) {
  const navigate = useNavigate();
  const [suggested, setSuggested] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open || !requestId) return;
    API.get("/hs/rams", { params: { entity_type: "job_request", entity_id: requestId } })
      .then(({ data }) => setSuggested((data.rams || []).filter(r => r.status !== "superseded"))).catch(() => setSuggested([]));
  }, [open, requestId]);

  useEffect(() => {
    if (!open || search.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      API.get("/hs/rams", { params: { search: search.trim() } })
        .then(({ data }) => setResults((data.rams || []).filter(r => r.status !== "superseded" && !suggested.some(s => s.id === r.id)).slice(0, 8))).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search, open, suggested]);

  const finish = (path) => { onClose(); navigate(path || `/jobs/${jobId}`); };

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await API.post("/hs/rams", { entity_type: "job", entity_id: jobId });
      toast.success(`${data.reference} created for ${jobRef || "the new job"}`);
      finish(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };

  const attach = async (r) => {
    setBusy(r.id);
    try {
      await API.post(`/hs/rams/${r.id}/pull`, { entity_type: "job", entity_id: jobId, overwrite_scope: false, overwrite_operatives: false });
      toast.success(`${r.reference} attached to ${jobRef || "the new job"}`);
      finish();
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };

  const Row = ({ r, hint }) => (
    <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs" data-testid={`rams-attach-row-${r.id}`}>
      <span className="font-mono">{r.reference}</span>
      <span className="truncate flex-1">{r.title}</span>
      {hint && <span className="text-[10px] text-muted-foreground hidden sm:inline">{hint}</span>}
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_CLASS[r.status] || "bg-slate-100"}`}>{r.status}</span>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => attach(r)} disabled={!!busy} data-testid={`rams-attach-${r.id}`}>
        <Paperclip size={12} className="mr-1" /> {busy === r.id ? "Attaching…" : "Attach"}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="rams-after-convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HardHat size={18} className="text-amber-600" /> RAMS for {jobRef || "the new job"}</DialogTitle>
          <DialogDescription>{autoPending ? "The job is created and the AI is already writing its RAMS in the background — it will appear on the job page in a few seconds as a draft for a supervisor to check and issue. You can still attach an existing RAMS instead." : "The job is created. Give it a RAMS now so the crew can be briefed before they arrive — or skip and add one later from the job page."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {autoPending && <p className="text-[11px] rounded-md bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-900/60 px-3 py-2 text-violet-900 dark:text-violet-200" data-testid="rams-after-convert-auto-note">AI RAMS in progress for {jobRef || "this job"} — creating another one by hand will give the job two RAMS.</p>}
          <Button onClick={create} disabled={!!busy} variant={autoPending ? "outline" : "default"} className="w-full justify-start" data-testid="rams-after-convert-create">
            <Plus size={16} className="mr-2" /> {busy === "create" ? "Creating…" : "Create a new RAMS for this job"}
            <span className="ml-auto text-[10px] opacity-80 hidden sm:inline">pulls client, site, scope, crew & nearest A&amp;E</span>
          </Button>
          {suggested.length > 0 && (
            <div className="space-y-1.5" data-testid="rams-after-convert-suggested">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Written for this request — attach to the job</p>
              {suggested.map(r => <Row key={r.id} r={r} hint="from the request" />)}
            </div>
          )}
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Attach another existing RAMS</p>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by reference, title, client or site…" className="h-8 text-xs pl-8" data-testid="rams-after-convert-search" />
            </div>
            {results.map(r => <Row key={r.id} r={r} hint={r.job?.reference ? `on ${r.job.reference}` : ""} />)}
            {search.trim().length >= 2 && results.length === 0 && <p className="text-xs text-muted-foreground px-1">No matching RAMS.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => finish()} disabled={!!busy} data-testid="rams-after-convert-skip">Skip for now — open the job</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
